import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SanitizedHtml } from "@/components/common/sanitized-html";
import { getWhatsNewCurrent, listWhatsNewArchive } from "@/features/knowledge/api";
import type { WhatsNewArchiveEntry } from "@/features/knowledge/types";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function WhatsNewPage() {
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState<WhatsNewArchiveEntry[]>([]);
  const [current, setCurrent] = useState<{ current: string; publishedAt: string | null } | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getWhatsNewCurrent()
      .then((c) => c.current && setCurrent(c))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(() => {
      listWhatsNewArchive(search)
        .then((data) => !cancelled && setEntries(data))
        .catch(() => !cancelled && toast.error("Could not load bulletins."))
        .finally(() => !cancelled && setLoading(false));
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search]);

  return (
    <div className="flex flex-col gap-6">
      {current?.current && (
        <Card className="p-5">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-semibold">Current bulletin</h2>
            {current.publishedAt && (
              <span className="text-muted-foreground text-xs">
                {formatDate(current.publishedAt)}
              </span>
            )}
          </div>
          <SanitizedHtml html={current.current} />
        </Card>
      )}

      <div className="relative max-w-xl">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search past bulletins…"
          className="pl-10"
          aria-label="Search What's New archive"
        />
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">No archived bulletins.</p>
      ) : (
        <Accordion type="single" collapsible className="w-full">
          {entries.map((entry) => (
            <AccordionItem key={entry.id} value={entry.id}>
              <AccordionTrigger className="text-left">
                <span className="flex flex-1 items-center justify-between gap-4 pr-2">
                  <span>{entry.title}</span>
                  <span className="text-muted-foreground text-xs font-normal">
                    {formatDate(entry.createdAt)}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <SanitizedHtml html={entry.body} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
